import { fetchContent } from "./config"
import { ContentfulProduct } from "@lib/types"
import type { GetStaticPropsContext } from "next"
import {
  TEXT_BLOCK_QUERY,
  BUTTON_QUERY,
  HEADER_QUERY,
  ANNOUNCEMENTBAR_QUERY,
  ANNOUNCEMENTBARS_QUERY,
  FOOTER_QUERY,
  PRODUCT_QUERY,
  CONTENTFUL_IMAGE_QUERY,
  PAGE_SLUGS_QUERY,
  LINK_QUERY,
} from "@lib/fragments"

export const getSite = async ({ preview, locale }: GetStaticPropsContext) => {
  const res = await fetchContent(
    `
  {
    siteCollection(limit: 1, preview: ${preview}, locale: "${locale}") {
      items {
        ${ANNOUNCEMENTBAR_QUERY}
        ${HEADER_QUERY}
        ${FOOTER_QUERY}
        seoTitleTemplate
        mobileSidebarBottomLinksCollection {
          items {
            ${LINK_QUERY}
          }
        }
        emptyCartButton {
          ${BUTTON_QUERY}
        }
        shippingThresholdsCollection {
          items {
            countryCode
            currency
            value
            text
          }
        }
        cartFooterTextFirstLine {
          ... on TextBlock {
             ${TEXT_BLOCK_QUERY}
          }
        }
        cartFooterTextSecondLine {
          ... on TextBlock {
            ${TEXT_BLOCK_QUERY}
          }
        }
        upsellCartCollection {
          items {
            ${PRODUCT_QUERY}
          }
        }
        giftWrapping 
        loginHeader {
          json
        }
        loginHeaderWishlist {
          json
        }
        loginImage {
          ${CONTENTFUL_IMAGE_QUERY}
        }
        loginImageMobile {
          ${CONTENTFUL_IMAGE_QUERY}
        }
        signUpHeader {
          json
        }
        signUpFooter {
          json
          links {
            entries {
              hyperlink {
                __typename
                sys {
                  id
                }
                ${PAGE_SLUGS_QUERY}
              }
            }
          }
        }
        signUpSuccessHeader {
          json
          links {
            assets {
              block {
                ${CONTENTFUL_IMAGE_QUERY}
              }
            }
          }
        }
        resetPasswordHeader {
          json
        }
        checkEmailHeader {
          json
          links {
            assets {
              block {
                ${CONTENTFUL_IMAGE_QUERY}
              }
            }
          }
        }
        createNewPasswordHeader {
          json
        }
        accountSidebarFooter {
          json
        }
        accountOrderFooter {
          json
        }
      }
    }
  }
  `,
    preview
  )

  const res2 = await fetchContent(
    `
  {
    siteCollection(limit: 1, preview: ${preview}, locale: "${locale}") {
      items {
        returnOrderText {
          json
          links {
            entries {
              hyperlink {
                __typename
                sys {
                  id
                }
                ${PAGE_SLUGS_QUERY}
              }
            }
          }
        }
        orderHistoryPlaceholder {
          json
          links {
            entries {
              block {
                ... on Collection {
                  __typename
                  sys {
                    id
                  }
                  slug
                  title
                  featuredImage {
                    ${CONTENTFUL_IMAGE_QUERY}
                  }
                }
              }
            }
          }
        }
        wishlistPlaceholder {
          json
          links {
            entries {
              block {
                ... on Collection {
                  __typename
                  sys {
                    id
                  }
                  slug
                  title
                  featuredImage {
                    ${CONTENTFUL_IMAGE_QUERY}
                  }
                }
              }
            }
          }
        }
        virtualClosetPlaceholder {
          json
          links {
            entries {
              block {
                ... on Collection {
                  __typename
                  sys {
                    id
                  }
                  slug
                  title
                  featuredImage {
                    ${CONTENTFUL_IMAGE_QUERY}
                  }
                }
              }
            }
          }
        }
        rewardsPageHeader {
          json
          links {
            entries {
              hyperlink {
                __typename
                sys {
                  id
                }
                ${PAGE_SLUGS_QUERY}
              }
            }
          }
        }
        pageNotFound {
          json
          links {
            entries {
              block {
                ... on Button {
                  ${BUTTON_QUERY}
                }
                ... on Collection {
                  __typename
                  sys {
                    id
                  }
                  slug
                  title
                  featuredImage {
                    ${CONTENTFUL_IMAGE_QUERY}
                  }
                }
              }
            }
          }
        }
        giftWrapperOptionAvailability
      }
    }
  }
  `,
    preview
  )

  const res3 = await fetchContent(
    `
    {
      siteCollection(limit: 1, preview: ${preview}, locale: "${locale}") {
        items {
          ${ANNOUNCEMENTBARS_QUERY}
          isAnnouncementHidden
          countriesCollection(limit: 20) {
            items {
              label
              image {
                url
                description
              }
              value
            }
          }
          currenciesCollection(limit: 20) {
            items {
              label
              image {
                url
                description
              }
              value
              countryCode
            }
          }
          switchCountryModal {
            richText {
              json
              links {
                assets {
                  block {  
                    ${CONTENTFUL_IMAGE_QUERY}
                  }
                }
              }
            }
            image {
              url
              description
            }
            imageMobile {
              url
              description
            }
            button {
              ${BUTTON_QUERY}
            }
            linkText 
            footerText
          }
          chooseGeoStoreModal {
            richText {
              json
              links {
                assets {
                  block {  
                    ${CONTENTFUL_IMAGE_QUERY}
                  }
                }
              }
            }
            image {
              url
              description
            }
            imageMobile {
              url
              description
            }
            buttonsCollection {
              items {
                ${BUTTON_QUERY}
              }
            }
            linkText 
            footerText
          }
          productNotAvailableModal {
            richText {
              json
              links {
                assets {
                  block {  
                    ${CONTENTFUL_IMAGE_QUERY}
                  }
                }
              }
            }
            image {
              url
              description
            }
            imageMobile {
              url
              description
            }
            button {
              ${BUTTON_QUERY}
            }
            linkText 
            footerText
          }
          collectionNotAvailableModal {
            richText {
              json
              links {
                assets {
                  block {  
                    ${CONTENTFUL_IMAGE_QUERY}
                  }
                }
              }
            }
            image {
              url
              description
            }
            imageMobile {
              url
              description
            }
            button {
              ${BUTTON_QUERY}
            }
            linkText 
            footerText
          }
        }
      }
    }
  `,
    preview
  )

  if (res?.siteCollection?.items) {
    const { items } = res.siteCollection
    const items2 = res2.siteCollection.items
    const items3 = res3.siteCollection.items

    const site = {
      ...items[0],
      ...items2[0],
      ...items3[0],
      upsellCartCollection: {
        items: items[0].upsellCartCollection.items.map(
          (product: ContentfulProduct) => {
            return {
              ...product,
              shopifyProduct: null,
            }
          }
        ),
      },
    }

    return site
  }
  console.log("Error returning siteCollection")
}
